import {uniq} from 'lodash';
import {ThankYouPageStore} from '../../domain/stores/ThankYouPageStore';
import {BaseController} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/controller-factory/BaseController';
import {SiteStore} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/site-store/SiteStore';
import {ControllerParams} from '@wix/yoshi-flow-editor';
import {ShippingService} from '../../domain/services/ShippingService';
import {NavigationService} from '../../domain/services/NavigationService';
import {NavigationStore} from '../../domain/stores/NavigationStore';
import {BIService} from '../../domain/services/BIService';
import {OrderService} from '../../domain/services/OrderService';
import {wixcodePublicApi} from './WixCode/wixcodePublicApi';
import {TrackEventService} from '../../domain/services/TrackEventService';
import {AddressTranslationService} from '../../domain/services/AddressTranslationService';
import {PaymentsService} from '../../domain/services/PaymentsService';
import {CartService} from '../../domain/services/CartService';
import {CheckoutService} from '../../domain/services/CheckoutService';
import {PaymentMethod, Item} from '../../types/app.types';
import {Specs} from '../../components/thankYouPage/constants';

export class ThankYouPageController extends BaseController {
  protected readonly thankYouPageStore: ThankYouPageStore;
  protected readonly navigationStore: NavigationStore;
  protected readonly orderService: OrderService;
  protected readonly cartService: CartService;
  protected readonly checkoutService: CheckoutService;
  protected readonly biService: BIService;
  protected readonly trackEventService: TrackEventService;
  protected readonly navigationService: NavigationService;

  constructor(controllerParams: ControllerParams) {
    super(controllerParams);
    const flowAPI = controllerParams.flowAPI;
    const siteStore: SiteStore = controllerParams.appData.context.siteStore;
    const addressTranslationService = new AddressTranslationService({siteStore});
    const paymentsService = new PaymentsService({siteStore});
    const orderService = new OrderService({siteStore, addressTranslationService});
    const shippingService = new ShippingService({flowAPI, orderService});
    const biService = new BIService({siteStore});
    const navigationService = new NavigationService({flowAPI, siteStore, biService, orderService});
    const trackEventService = new TrackEventService({siteStore, orderService});

    this.navigationStore = new NavigationStore({navigationService});
    this.thankYouPageStore = new ThankYouPageStore({
      biService,
      shippingService,
      orderService,
      paymentsService,
      siteStore,
    });

    this.orderService = orderService;
    this.biService = biService;
    this.trackEventService = trackEventService;
    this.cartService = new CartService({siteStore});
    this.checkoutService = new CheckoutService({siteStore});
    this.navigationService = navigationService;
  }

  public readonly load = async () => {
    try {
      await this.orderService.fetchOrder();
    } catch (e) {
      /* istanbul ignore else: todo: test */
      // @ts-expect-error
      if (this.siteStore.isSSR() && e.message.includes('PERMISSION_DENIED')) {
        return this.setProps({ssrError: true});
      }
      /* istanbul ignore next: ignore exception flow */
      throw e;
    }

    void (await this.checkoutService.markCheckoutAsCompleted(this.orderService.checkoutId));
    void (await this.cartService.removeCart(this.orderService.cartId));
    void this.reportOnLoad();

    this.setProps({
      thankYouPageStore: await this.thankYouPageStore.toProps(),
      navigationStore: await this.navigationStore.toProps(),
    });
  };

  public readonly init = () => this.load();

  public readonly onStyleUpdate = (styleParams: {}) => {
    return styleParams;
  };

  /* istanbul ignore next */
  public readonly exports = () => {
    return wixcodePublicApi({siteStore: this.siteStore});
  };

  public getFreeTexts = (): string[] => [];

  private reportOnLoad() {
    if (this.siteStore.isSSR()) {
      return;
    }

    this.trackEventService.trackPurchase(this.orderService.trackPurchaseParams);

    const {paymentMethodType, itemType, numOfLineItems} = this.extractDataFromItemsForBI(this.orderService.items);
    this.biService.thankYouPageLoadSf({
      orderId: this.orderService.orderGuid,
      cartId: this.orderService.cartId,
      checkoutId: this.orderService.checkoutId,
      shippingMethodType: this.orderService.deliveryType || '',
      cartType: this.orderService.cartType,
      paymentStatus: this.orderService.paymentStatus,
      paymentProvider: this.orderService.paymentMethod,
      numOfLineItems,
      itemType,
      paymentMethodType,
    });
  }

  private extractDataFromItemsForBI(items: Item[]) {
    const {productIds, itemTypes, paymentOptions} = items.reduce(
      (acc, curr) => {
        return {
          ...acc,
          productIds: [...acc.productIds, curr.productId],
          itemTypes: [...acc.itemTypes, curr.productType],
          paymentOptions: [...acc.paymentOptions, curr.paymentOption],
        };
      },
      {productIds: [], itemTypes: [], paymentOptions: []}
    );

    return {
      numOfLineItems: uniq(productIds).length,
      itemType: uniq(itemTypes).join(','),
      paymentMethodType: this.getPaymentMethodTypeForBI(paymentOptions as string[]),
    };
  }

  private getPaymentMethodTypeForBI(paymentOptions: string[]) {
    const shouldUsePlatformBILogger =
      this.siteStore.experiments.enabled(Specs.EnablePlatformBiLoggerTYP) &&
      this.siteStore.experiments.enabled(Specs.EnableNewTyp);

    if (shouldUsePlatformBILogger) {
      return uniq(paymentOptions).join(',');
    }

    return this.orderService.isOfflinePayment ? PaymentMethod.Offline : PaymentMethod.Online;
  }
}
